import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Loader2, Trophy, Info, Settings as SettingsIcon } from 'lucide-react';
import { ResetCacheButton } from './ResetCacheButton';
import ScheduleSettingsDialog from './ScheduleSettingsDialog';
import ScheduleTemplatesPanel from './ScheduleTemplatesPanel';
import BookingCalendarPanel from './booking/BookingCalendarPanel';
import BookMeProposalsPanel from './BookMeProposalsPanel';
import SurveysEventsPanel from './SurveysEventsPanel';
import SchedulerPanel from './SchedulerPanel';
import AvailabilityPanel from './AvailabilityPanel';

/**
 * SchedulerHub — the Scheduler role's workspace.
 *
 * Layout (SCHEDULER_ROLE_PAGE.md):
 *
 *   [ common row: Reset Cache · Settings ]         sport-independent (sports are added by the AD, Org Teams)
 *   [ Basketball | Baseball | Volleyball ]         ONE tab per configured sport
 *      └ Schedule | Calendar | Requests | Blocked | Interest | Availability  (Messaging moved to the Communications top nav, 2026-10-12)
 *
 * The sport tabs are built from the org's own configured sports
 * (GET /api/game-schedule/{org}/sports, sourced from the `sports` collection) —
 * never a hardcoded list. Basketball is ordered first and is the landing tab when
 * the org has it; otherwise the first configured sport wins.
 *
 * Because each tab IS a sport, the in-grid sport picker is redundant and is
 * suppressed via GameSchedulePanel's `lockedSport`. "WP Mgmt" stays a top-level
 * Dashboard tab because it is sport-independent.
 */

// Only the ORDER is opinionated here — never the membership of the list.
const FIRST_SPORT = 'bk';

export function orderSports(sports) {
  const list = sports || [];
  const first = list.filter(s => s.slug === FIRST_SPORT);
  const rest = list.filter(s => s.slug !== FIRST_SPORT)
    .sort((a, b) => (a.label || a.slug || '').localeCompare(b.label || b.slug || ''));
  return [...first, ...rest];
}

// The five views SchedulerPanel owns, plus two that render their own panel.
const SCHEDULER_VIEWS = ['schedule', 'calendar', 'pending', 'blocked', 'interests'];

const SUB_TABS = [
  { value: 'schedule',     label: 'Schedule' },
  { value: 'calendar',     label: 'Calendar' },
  { value: 'pending',      label: 'Requests' },
  { value: 'blocked',      label: 'Blocked' },
  { value: 'interests',    label: 'Interest' },
  { value: 'bookme',       label: 'BookMe' },
  { value: 'templates',    label: 'Templates' },
  { value: 'availability', label: 'Availability' },
];

// Panels that do not yet accept a `sport` prop, so their content is still
// org-wide. The banner below says so rather than letting the sport tab imply a
// scoping that does not exist. Both are rebuilt with the Calendly (OL-BookMe)
// work — see SCHEDULER_ROLE_PAGE.md §4.
const NOT_YET_SPORT_SCOPED = {
  bookme: 'Proposals below are scoped to this sport. The public booking page underneath is organization-wide — an event type carries its own sport options.',
  availability: 'Availability is still organization-wide — it is not filtered to this sport yet.',
};

export default function SchedulerHub({ orgId, teams, userRole, user, familyId, isDark = true }) {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [activeSport, setActiveSport] = useState('');
  const [subTab, setSubTab] = useState('schedule');
  const [settingsOpen, setSettingsOpen] = useState(false);

  const loadSports = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setErr('');
    try {
      const { data } = await axios.get(`${API_URL}/api/game-schedule/${orgId}/sports`, { withCredentials: true });
      setSports(data?.sports || []);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not load this organization’s sports.');
    } finally {
      setLoading(false);
    }
  }, [orgId, API_URL]);

  useEffect(() => { loadSports(); }, [loadSports]);

  const ordered = useMemo(() => orderSports(sports), [sports]);

  // Land on Basketball when the org has it, else the first configured sport.
  // Also self-heals if the active sport is removed from the org's config.
  useEffect(() => {
    if (ordered.length === 0) return;
    if (!ordered.some(s => s.slug === activeSport)) setActiveSport(ordered[0].slug);
  }, [ordered, activeSport]);

  if (!orgId) {
    return (
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
        <CardContent className="p-8 text-center">
          <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization available</p>
        </CardContent>
      </Card>
    );
  }

  const muted = isDark ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900';
  const dim = isDark ? 'text-slate-500' : 'text-slate-400';
  const activeLabel = ordered.find(s => s.slug === activeSport)?.label || '';
  const banner = NOT_YET_SPORT_SCOPED[subTab];

  return (
    <div className="space-y-3" data-testid="scheduler-hub">
      {/* ── COMMON ROW — everything here is sport-independent ───────────── */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="scheduler-common-row">
        <ResetCacheButton orgId={orgId} />
        <Button size="sm" variant="outline" onClick={() => setSettingsOpen(true)}
          className={`h-7 text-xs ${isDark ? 'border-slate-600 text-slate-300' : ''}`}
          title="Referee agency, Google Calendars and game durations — these apply to every sport"
          data-testid="scheduler-settings-btn">
          <SettingsIcon className="h-3 w-3 mr-1" />Settings
        </Button>
        <span className={`text-[11px] ml-auto ${dim}`}>
          Each sport below has its own schedule, calendar and scheduling conversations.
        </span>
      </div>

      {/* ── SPORT TABS — one per configured sport, Basketball first ─────── */}
      {loading ? (
        <div className={`flex items-center gap-2 text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
          <Loader2 className="h-3 w-3 animate-spin" /> Loading sports…
        </div>
      ) : err ? (
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
          <CardContent className="p-4 text-center space-y-2">
            <p className="text-xs text-red-400">{err}</p>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={loadSports}
              data-testid="scheduler-sports-retry">Retry</Button>
          </CardContent>
        </Card>
      ) : ordered.length === 0 ? (
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
          <CardContent className="p-8 text-center">
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              No sports are configured for this organization yet.
            </p>
            <p className={`text-xs mt-1 ${dim}`}>
              The Athletic Director adds sports (Org Teams); each one gets a tab here.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className={`flex gap-1 flex-wrap border-b pb-2 ${isDark ? 'border-slate-700' : 'border-slate-200'}`}
               data-testid="scheduler-sport-tabs">
            {ordered.map(s => (
              <Button key={s.slug} size="sm"
                variant={activeSport === s.slug ? 'default' : 'ghost'}
                className={activeSport === s.slug
                  ? 'bg-purple-600 hover:bg-purple-700 text-white h-8 text-xs'
                  : `${muted} h-8 text-xs`}
                onClick={() => { setActiveSport(s.slug); setSubTab('schedule'); }}
                data-testid={`scheduler-sport-tab-${s.slug}`}>
                <Trophy className="h-3 w-3 mr-1" />{s.label}
              </Button>
            ))}
          </div>

          {/* ── PER-SPORT SUB-TABS ───────────────────────────────────────── */}
          <div className="flex gap-1 flex-wrap" data-testid="scheduler-sub-tabs">
            {SUB_TABS.map(t => (
              <Button key={t.value} size="sm"
                variant={subTab === t.value ? 'default' : 'ghost'}
                className={subTab === t.value
                  ? 'bg-amber-600 hover:bg-amber-700 text-white h-7 text-xs'
                  : `${muted} h-7 text-xs`}
                onClick={() => setSubTab(t.value)}
                data-testid={`scheduler-sub-tab-${t.value}`}>
                {t.label}
              </Button>
            ))}
          </div>

          {banner && (
            <div className={`flex items-start gap-2 text-[11px] rounded-md border px-3 py-2 ${
              isDark ? 'border-amber-700/40 bg-amber-900/10 text-amber-300'
                     : 'border-amber-300 bg-amber-50 text-amber-700'}`}
              data-testid="scheduler-scope-banner">
              <Info className="h-3.5 w-3.5 mt-[1px] shrink-0" />
              <span>{banner}</span>
            </div>
          )}

          {/* Remount on sport change (key) so every panel refetches for the newly
              selected sport instead of showing the previous one's rows. */}
          <div key={activeSport}>
            {SCHEDULER_VIEWS.includes(subTab) && (
              <SchedulerPanel
                orgId={orgId}
                teams={teams}
                userRole={userRole}
                sport={activeSport}
                view={subTab}
                hideViewBar
              />
            )}

            {/* Availability + RSVP were two separate Dashboard tabs rendering the
                SAME AvailabilityPanel. Collapsed into this one entry. */}
            {subTab === 'templates' && (
              <ScheduleTemplatesPanel
                orgId={orgId}
                sport={activeSport}
                sportLabel={activeLabel}
                isDark={isDark}
              />
            )}

            {/* OL BookMe — scheduler-to-scheduler booking (OL_BOOKME.md). The panel
                was already wired for the scheduler role but was UNREACHABLE: the
                Dashboard `calendar` tab's role list omits `scheduler`. Rendering it
                here is what actually exposes it. */}
            {subTab === 'bookme' && (
              <div className="space-y-6">
                {/* The core: multi-date proposals to another school (OL_BOOKME.md §4). */}
                <BookMeProposalsPanel
                  orgId={orgId}
                  sport={activeSport}
                  sportLabel={activeLabel}
                  isDark={isDark}
                />
                {/* Org-wide: surveys and non-game events (OL_BOOKME.md §6e). */}
                <div className={`pt-4 border-t ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                  <div className={`text-xs font-semibold mb-2 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                    Surveys &amp; org events
                  </div>
                  <SurveysEventsPanel orgId={orgId} isDark={isDark} />
                </div>
                {/* The existing public booking surface (event types + requests inbox). */}
                <div className={`pt-4 border-t ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                  <div className={`text-xs font-semibold mb-2 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                    Public booking page
                  </div>
                  <BookingCalendarPanel orgId={orgId} />
                </div>
              </div>
            )}

            {subTab === 'availability' && (
              <AvailabilityPanel
                orgId={orgId}
                teams={teams}
                userRole={userRole}
                familyId={familyId}
              />
            )}

          </div>

          {!banner && (
            <p className={`text-[11px] ${dim}`}>
              Showing <span className="font-medium">{activeLabel}</span> only — this tab is scoped to that sport.
            </p>
          )}
        </>
      )}

      {/* Rendered here, not inside a sub-tab, so Settings opens from any of them
          (the grid is only mounted on Schedule/Calendar). */}
      <ScheduleSettingsDialog
        orgId={orgId}
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        isDark={isDark}
      />

    </div>
  );
}
